import Papa from 'papaparse';
import type { Curve, Event, ParsedData } from '../../../interfaces/toolData';
import type { WaveFileType } from '../types';
import { cleanTimeSeriesData } from '../../dataQuality';

const FILE_TYPE: WaveFileType = 'RIME_PULSER_DRIVER_DEFAULT';

/**
 * Parser for RIME Pulser Driver CSV logs
 * - CSV with a single header row (no preamble)
 * - Primary time column: "Timestamp" in M/D/YYYY h:mm:ss AM/PM (assumed local/CST)
 * - Mixture of sparse time-series columns and memo/event columns
 */
export class RimeParser {
  /** Quick content signature check */
  static canParse(content: string): boolean {
    if (!content || !content.trim()) return false;
    const firstLine = content.split('\n').find(l => l.includes(',')) || '';
    const header = firstLine.toLowerCase();
    // Required anchors
    const hasTimestamp = header.includes('timestamp');
    const hasIndex = header.includes('index');
    const hasPowerCycle = header.includes('powercycle');
    // Distinctive columns seen in fixtures
    const hasMemoPump = header.includes('memo_pump_event');
    const hasVibe = header.includes('x vibration') || header.includes('y vibration') || header.includes('axial vibration');
    const hasFlowSwitch = header.includes('flow switch x') || header.includes('flow switch y') || header.includes('flow switch z');
    const hasGamma = header.includes('gamma');
    const hasRot = header.includes('rotational speed');

    return hasTimestamp && hasIndex && hasPowerCycle && (hasMemoPump || hasVibe || hasFlowSwitch || hasGamma || hasRot);
  }

  /** Parse full CSV into standardized ParsedData */
  static parse(fileContent: string, filename: string = ''): ParsedData {
    if (!fileContent || !fileContent.trim()) {
      throw new Error('File is empty');
    }

    const parseResult = Papa.parse(fileContent, {
      header: true,
      skipEmptyLines: true,
      dynamicTyping: false
    });

    if (parseResult.errors.length > 0) {
      // Still proceed but record the first error;
      // some rows can be malformed while others are usable
      // We'll store all messages in ParsedData.errors
    }

    const rows = (parseResult.data as Record<string, string>[]) || [];
    if (rows.length === 0) {
      throw new Error('No data rows found in RIME file');
    }

    // Columns of interest (best-effort units per product knowledge)
    const columnConfigs: Array<{ key: string; name: string; unit: string }> = [
      { key: 'Temperature', name: 'Temperature', unit: '°C' },
      { key: 'Voltage', name: 'Voltage', unit: 'V' },
      { key: 'X Vibration', name: 'X Vibration', unit: 'G rms' },
      { key: 'Y Vibration', name: 'Y Vibration', unit: 'G rms' },
      { key: 'Axial Vibration', name: 'Axial Vibration', unit: 'G rms' },
      { key: 'Lateral Vibration', name: 'Lateral Vibration', unit: 'G rms' },
      { key: 'Gamma', name: 'Gamma', unit: 'cps' },
      { key: 'Pulse Energy', name: 'Pulse Energy', unit: 'mA·s' },
      { key: 'Pulse Open Time', name: 'Pulse Open Time', unit: 'ms' },
      { key: 'Pulse Close Time', name: 'Pulse Close Time', unit: 'ms' },
      { key: 'Pulse Width', name: 'Pulse Width', unit: 'ms' },
      { key: 'Flow Switch X', name: 'Flow Switch X', unit: '' },
      { key: 'Flow Switch Y', name: 'Flow Switch Y', unit: '' },
      { key: 'Flow Switch Z', name: 'Flow Switch Z', unit: '' },
      { key: '>10G X Shock Counts', name: '>10G X Shock Counts', unit: 'counts' },
      { key: '>20G X Shock Counts', name: '>20G X Shock Counts', unit: 'counts' },
      { key: '>10G Y Shock Counts', name: '>10G Y Shock Counts', unit: 'counts' },
      { key: '>20G Y Shock Counts', name: '>20G Y Shock Counts', unit: 'counts' },
      { key: '>10G Z Shock Counts', name: '>10G Z Shock Counts', unit: 'counts' },
      { key: '>20G Z Shock Counts', name: '>20G Z Shock Counts', unit: 'counts' },
      { key: 'Peak +ve X Shock', name: 'Peak +ve X Shock', unit: 'G' },
      { key: 'Peak -ve X Shock', name: 'Peak -ve X Shock', unit: 'G' },
      { key: 'Peak +ve Y Shock', name: 'Peak +ve Y Shock', unit: 'G' },
      { key: 'Peak -ve Y Shock', name: 'Peak -ve Y Shock', unit: 'G' },
      { key: 'Peak +ve Z Shock', name: 'Peak +ve Z Shock', unit: 'G' },
      { key: 'Peak -ve Z Shock', name: 'Peak -ve Z Shock', unit: 'G' },
      { key: 'Rotational Speed', name: 'Rotational Speed', unit: 'RPM' },
      // Memory is sometimes numeric, sometimes may be named events; handle both
      { key: 'Memory', name: 'Memory', unit: '' }
    ];

    const curves: Curve[] = [];
    const columnInfo: Record<string, { unit: string; description?: string }> = {};

    // Prepare event collections
    const pumpOnTimes: Date[] = [];
    const pumpOffTimes: Date[] = [];
    const pumpStateTimes: Date[] = [];
    const pumpStateValues: number[] = [];
    const statusCodeBuckets: Record<string, Date[]> = {};
    const memoryEventBuckets: Record<string, Date[]> = {};

    // Build per-column numeric arrays
    const series: Record<string, { times: Date[]; values: number[]; unit: string; name: string }> = {};
    columnConfigs.forEach(c => {
      series[c.key] = { times: [], values: [], unit: c.unit, name: c.name };
      columnInfo[c.name] = { unit: c.unit };
    });

    // Parse rows
    for (const row of rows) {
      const tsRaw = (row['Timestamp'] || row['timestamp'] || '').toString().trim();
      if (!tsRaw) continue;
      const ts = RimeParser.parseChicagoLocal(tsRaw);
      if (!ts || isNaN(ts.getTime())) continue;

      // Handle memo pump events
      const pumpMemo = (row['memo_Pump_Event'] || row['PUMP_EVENT'] || '').toString().trim();
      if (pumpMemo) {
        if (/pumps\s*on/i.test(pumpMemo)) {
          pumpOnTimes.push(ts);
          pumpStateTimes.push(ts); pumpStateValues.push(1);
        } else if (/pumps\s*off/i.test(pumpMemo)) {
          pumpOffTimes.push(ts);
          pumpStateTimes.push(ts); pumpStateValues.push(0);
        }
      }

      // Handle status code events (separate groups per distinct code/text)
      const statusCode = (row['memo_Status_Code'] || row['Status_Code'] || '').toString().trim();
      if (statusCode) {
        const key = `Status: ${statusCode}`;
        statusCodeBuckets[key] = statusCodeBuckets[key] || [];
        statusCodeBuckets[key].push(ts);
      }

      // Memory may be numeric time series or discrete event names
      if (row['Memory'] !== undefined && row['Memory'] !== null && row['Memory'].toString().trim() !== '') {
        const memStr = row['Memory'].toString().trim();
        const memVal = parseFloat(memStr);
        if (!isNaN(memVal)) {
          series['Memory'].times.push(ts);
          series['Memory'].values.push(memVal);
        } else {
          const key = `Memory: ${memStr}`;
          memoryEventBuckets[key] = memoryEventBuckets[key] || [];
          memoryEventBuckets[key].push(ts);
        }
      }

      // Numeric series extraction for configured columns
      for (const cfg of columnConfigs) {
        if (cfg.key === 'Memory') continue; // already handled
        const raw = row[cfg.key];
        if (raw === undefined || raw === null) continue;
        const str = raw.toString().trim();
        if (!str) continue;
        const val = parseFloat(str);
        if (!isNaN(val) && isFinite(val)) {
          series[cfg.key].times.push(ts);
          series[cfg.key].values.push(val);
        }
      }
    }

    // Convert collected series into curves, applying cleanup
    Object.values(series).forEach(s => {
      if (s.times.length === 0) return;
      const rawCurve: Curve = { name: s.name, xValues: s.times, yValues: s.values, yUnit: s.unit };
      const cleaned = cleanTimeSeriesData(rawCurve).cleanedCurve;
      if (cleaned.xValues.length > 0) {
        curves.push(cleaned);
      }
    });

    // Derived energy curve in Joules when Pulse Energy (mA·s) exists
    const energySeries = series['Pulse Energy'];
    if (energySeries && energySeries.times.length > 0) {
      const joulesCurve: Curve = {
        name: 'Pulse Energy (J)',
        xValues: energySeries.times,
        yValues: energySeries.values.map(v => v * 0.024), // 24 V * (mA·s)/1000
        yUnit: 'J'
      };
      const cleaned = cleanTimeSeriesData(joulesCurve).cleanedCurve;
      if (cleaned.xValues.length > 0) {
        curves.push(cleaned);
        columnInfo['Pulse Energy (J)'] = { unit: 'J', description: 'Derived at 24V nominal from Pulse Energy (mA·s)' };
      }
    }

    // Add Pump State curve if we captured any transitions
    if (pumpStateTimes.length > 0) {
      const pumpCurve: Curve = {
        name: 'Pump State',
        xValues: pumpStateTimes,
        yValues: pumpStateValues,
        yUnit: ''
      };
      const cleaned = cleanTimeSeriesData(pumpCurve).cleanedCurve;
      if (cleaned.xValues.length > 0) curves.push(cleaned);
    }

    // Build events
    const events: Event[] = [];
    if (pumpOnTimes.length > 0) {
      events.push({ name: 'Pumps On Event', xValues: pumpOnTimes });
    }
    if (pumpOffTimes.length > 0) {
      events.push({ name: 'Pumps Off Event', xValues: pumpOffTimes });
    }
    for (const [name, times] of Object.entries(statusCodeBuckets)) {
      events.push({ name, xValues: times });
    }
    for (const [name, times] of Object.entries(memoryEventBuckets)) {
      events.push({ name, xValues: times });
    }

    // Basic metadata
    const metadata: Record<string, string> = {
      manufacturer: 'RIME',
      product: 'Pulser Driver',
      filename: filename || ''
    };

    // Preview: number of usable rows across any curve
    const totalCount = curves.reduce((acc, c) => Math.max(acc, c.xValues.length), 0);

    return {
      toolType: 'RIME',
      fileType: FILE_TYPE,
      curves,
      events: events.length > 0 ? events : undefined,
      columnInfo,
      metadata,
      dataPreview: {
        totalCount
      },
      errors: parseResult.errors.map(e => e.message)
    };
  }

  // Parse timestamps like "M/D/YYYY h:mm:ss AM/PM" as America/Chicago local time
  private static parseChicagoLocal(s: string): Date | null {
    // Match 1-2 digit month/day, 4-digit year, 1-2 digit hour:minute:second, and AM/PM
    const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2}):(\d{2})\s*(AM|PM)$/i)
    if (!m) return null
    let month = parseInt(m[1], 10) - 1
    const day = parseInt(m[2], 10)
    const year = parseInt(m[3], 10)
    let hour = parseInt(m[4], 10)
    const minute = parseInt(m[5], 10)
    const second = parseInt(m[6], 10)
    const ampm = m[7].toUpperCase()

    if (ampm === 'PM' && hour < 12) hour += 12
    if (ampm === 'AM' && hour === 12) hour = 0

    const offset = RimeParser.getChicagoOffsetHours(year, month, day)
    const ms = Date.UTC(year, month, day, hour + offset, minute, second)
    return new Date(ms)
  }

  private static getChicagoOffsetHours(year: number, month: number, day: number): number {
    return RimeParser.isUsDstInChicago(year, month, day) ? 5 : 6
  }

  private static isUsDstInChicago(year: number, month: number, day: number): boolean {
    const secondSundayInMarch = RimeParser.nthDowOfMonth(year, 2, 0, 2)
    const firstSundayInNov = RimeParser.nthDowOfMonth(year, 10, 0, 1)
    const current = Date.UTC(year, month, day)
    const dstStart = Date.UTC(year, 2, secondSundayInMarch, 2, 0, 0)
    const dstEnd = Date.UTC(year, 10, firstSundayInNov, 2, 0, 0)
    return current >= dstStart && current < dstEnd
  }

  private static nthDowOfMonth(year: number, month: number, dow: number, n: number): number {
    const firstOfMonth = new Date(Date.UTC(year, month, 1))
    const firstDow = firstOfMonth.getUTCDay()
    let day = 1 + ((7 + dow - firstDow) % 7)
    day += (n - 1) * 7
    return day
  }
}
